"use client";

import { useEffect, useState, useTransition } from "react";
import { BookmarkCheck, BookmarkPlus } from "lucide-react";
import {
  addFavourite,
  removeFavourite,
  type FavouriteSpotType,
} from "@/app/actions/favourites";
import {
  addFestivalSave,
  removeFestivalSave,
} from "@/app/actions/festival-saves";
import {
  FAVOURITE_BUTTON_TOUCH_TARGET_CLASS,
  favouriteButtonLabel,
  type FavouritePendingAction,
} from "./favourite-button-state";
import {
  conditionSaveSignupSport,
  saveToCompanionConfirmation,
  type SaveToCompanionKind,
} from "./save-to-companion-state";
import {
  buildCompanionSignupHref,
  buildConditionSaveSignupIntentInput,
  writeCompanionSignupIntent,
} from "@/lib/companion/signup-intent";
import {
  SavedSpotCallPreferences,
  type SavedSpotCallPreferenceConfig,
} from "./SavedSpotCallPreferences";

type SaveTone = "light" | "dark" | "compact";

type Props = {
  id: string;
  kind?: SaveToCompanionKind;
  spotType?: FavouriteSpotType;
  initialSaved?: boolean;
  initialLoggedIn?: boolean | null;
  showConfirmation?: boolean;
  tone?: SaveTone;
  className?: string;
  callPreference?: SavedSpotCallPreferenceConfig;
};

type SaveStateResponse = {
  isLoggedIn: boolean;
  saved: boolean;
  callSports?: string[];
};

export function SaveToCompanionButton({
  id,
  kind = "condition",
  spotType,
  initialSaved = false,
  initialLoggedIn = null,
  showConfirmation = false,
  tone = "light",
  className = "",
  callPreference,
}: Props) {
  const [isLoggedIn, setIsLoggedIn] = useState(initialLoggedIn ?? false);
  const [saved, setSaved] = useState(initialSaved);
  const [callSports, setCallSports] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(initialLoggedIn !== null);
  const [message, setMessage] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [pendingAction, setPendingAction] = useState<FavouritePendingAction>(null);
  const [isPending, startTransition] = useTransition();
  const effectiveSpotType = spotType ?? "surf";
  const signupSport = conditionSaveSignupSport(
    spotType,
    callPreference?.allowedSupProfiles,
  );

  useEffect(() => {
    let ignore = false;
    const params = new URLSearchParams({ kind, id });
    fetch(`/api/companion/save-state?${params.toString()}`, {
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Save state unavailable");
        }
        return response.json() as Promise<SaveStateResponse>;
      })
      .then((data: SaveStateResponse) => {
        if (ignore) return;
        setIsLoggedIn(data.isLoggedIn);
        setSaved(data.saved);
        setCallSports(data.callSports ?? []);
        setMessage(null);
      })
      .catch(() => {
        if (ignore) return;
        setMessage({
          kind: "error",
          text: "Could not check whether this is saved yet. Try again in a moment.",
        });
      })
      .finally(() => {
        if (!ignore) setLoaded(true);
      });
    return () => {
      ignore = true;
    };
  }, [id, kind]);

  const label = favouriteButtonLabel({
    isLoggedIn,
    favourited: saved,
    pendingAction: isPending ? pendingAction : null,
  });
  const Icon = saved ? BookmarkCheck : BookmarkPlus;

  function handleClick() {
    setMessage(null);

    if (!isLoggedIn) {
      const signupIntent = kind === "condition"
        ? buildConditionSaveSignupIntentInput({ spotSlug: id, sport: signupSport })
        : {};
      writeCompanionSignupIntent(signupIntent);
      window.location.href = buildCompanionSignupHref(signupIntent);
      return;
    }

    const next = !saved;
    setSaved(next);
    setPendingAction(next ? "add" : "remove");

    startTransition(async () => {
      const result =
        kind === "festival"
          ? next
            ? await addFestivalSave(id)
            : await removeFestivalSave(id)
          : next
            ? await addFavourite(id, effectiveSpotType)
            : await removeFavourite(id, effectiveSpotType);

      if (!result.success) {
        setSaved(!next);
        setMessage({
          kind: "error",
          text: result.error ?? "Something went wrong. Please try again.",
        });
      } else if (next && showConfirmation) {
        setMessage({
          kind: "success",
          text: saveToCompanionConfirmation(kind),
        });
      }
      setPendingAction(null);
    });
  }

  return (
    <div className={`relative ${className}`.trim()}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isPending || !loaded}
        className={[
          `inline-flex ${FAVOURITE_BUTTON_TOUCH_TARGET_CLASS} items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60`,
          toneClass(tone, saved),
        ].join(" ")}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {loaded ? label : "Save to Companion"}
      </button>
      {message && !(message.kind === "success" && callPreference && saved && isLoggedIn) ? (
        <p
          role="status"
          className={`mt-2 max-w-[280px] text-[11px] leading-snug ${messageClass(
            tone,
            message.kind,
          )}`}
        >
          {message.text}
        </p>
      ) : null}
      {kind === "condition" && callPreference && saved && isLoggedIn ? (
        <SavedSpotCallPreferences
          spotId={id}
          spotType={effectiveSpotType}
          spotName={callPreference.spotName}
          category={callPreference.category}
          allowedSupProfiles={callPreference.allowedSupProfiles}
          initialCallSports={callSports}
        />
      ) : null}
    </div>
  );
}

function toneClass(tone: SaveTone, saved: boolean): string {
  if (tone === "dark") {
    return saved
      ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
      : "border-white/24 bg-white/10 text-white hover:bg-white/18";
  }
  if (tone === "compact") {
    return saved
      ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
      : "border-border bg-white text-navy hover:border-gold/50";
  }
  return saved
    ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
    : "border-border bg-white text-navy hover:border-gold/50";
}

function messageClass(tone: SaveTone, kind: "success" | "error"): string {
  if (tone === "dark") {
    return kind === "error" ? "text-red-200" : "text-emerald-100";
  }
  return kind === "error" ? "text-red-600" : "text-emerald-700";
}
